/**
 * The camo strip: challenge.ts's own picker, factored out once lobby.ts
 * needed the identical thing rather than a second copy of it.
 *
 * This is the one piece of match setup that is not "duplicate the
 * rendering, don't force it to share" the way lobby.ts's own module doc
 * calls out for the rest of the two screens' overlap (seat/lineup rows, the
 * list markup) — `scenarioPool`/`ChallengeMode` was already the one thing
 * genuinely shared between them, and this is the second: the strip, its
 * three-state selection (`undefined`/`null`/a record key), and the network
 * read behind it are identical on both screens down to the copy, so sharing
 * it is the same call `scenario-pool.ts` already made, not a new one.
 *
 * What differs between the two screens is what a selection *means*, and
 * that is deliberately left to each caller through `onChange`, not decided
 * here: challenge.ts sends `picker.rkey` once, at launch, as that match's
 * own seat plan and nothing else. lobby.ts has no launch-time seat plan for
 * a human seat to carry a camo on — `claim_seat` takes no camo field, and
 * `MatchPlayer` has none — so it instead treats a *user-initiated* change
 * as "make this my account default", the same `setDefaultCamo` write the
 * camo editor's own "Set as default" already makes, since that is exactly
 * what `lobby_seats_to_players` reads at Deploy time. `onChange`'s
 * `userInitiated` flag is what tells the two apart: an auto-preselect (the
 * strip choosing the account's existing default, or None when there is
 * nothing to choose) must not itself trigger a write, or opening a lobby
 * would silently set a default that was never asked for.
 */

import { type Session } from "../api";
import { DESTINATIONS } from "../destinations";
import { el } from "../dom";

// Where the editor lives, read off the same list the masthead is built from
// rather than written down a second time. A hash on this page, so following it
// is a screen change and not a page load.
const CAMO_HREF = DESTINATIONS.find((d) => d.id === "camo")!.href;

/**
 * Saved camos to a page.
 *
 * A collection has no ceiling and the strip had none either, so a player who
 * had made thirty of them got thirty tiles and a block that pushed whatever
 * sat under it off the screen. Twelve is two rows at the width the dialog
 * opens at — enough that most collections never see a pager at all, and
 * little enough that the block keeps its height when one does.
 *
 * None, the roll and the way to the editor are not in it. Those are always
 * reachable, whatever page of saved camo is showing.
 */
const PAGE_SIZE = 12;

/**
 * A die, drawn rather than typed.
 *
 * U+2684 is not in the site's typeface and nothing in the fallback chain
 * carries it either, so it came out as the browser's missing-character box:
 * a hollow rectangle with a cross in it, sitting in the corner of the Random
 * tile and reading as a picture that failed to load. A rounded square and
 * five pips is the same mark and needs no font to have it.
 */
function dieMark(): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("class", "camo-die-mark");
  svg.setAttribute("aria-hidden", "true");
  const box = document.createElementNS(NS, "rect");
  box.setAttribute("x", "1.6");
  box.setAttribute("y", "1.6");
  box.setAttribute("width", "12.8");
  box.setAttribute("height", "12.8");
  box.setAttribute("rx", "3");
  box.setAttribute("fill", "none");
  box.setAttribute("stroke", "currentColor");
  box.setAttribute("stroke-width", "1.3");
  svg.append(box);
  for (const [cx, cy] of [
    [5, 5],
    [11, 5],
    [8, 8],
    [5, 11],
    [11, 11],
  ]) {
    const pip = document.createElementNS(NS, "circle");
    pip.setAttribute("cx", String(cx));
    pip.setAttribute("cy", String(cy));
    pip.setAttribute("r", "1.35");
    pip.setAttribute("fill", "currentColor");
    svg.append(pip);
  }
  return svg;
}

/**
 * The picker's block, and the choice standing in it.
 *
 * `rkey` is read at the moment a caller needs it rather than captured once:
 * the list arrives after the screen does, and the selection changes under
 * it.
 *
 * Three states, not two. `undefined` is "I did not choose", which is what a
 * caller that launches or claims before the list arrives sees. `null` is
 * "no camo this match", which a player picking None means and which must
 * not be confused with the first. A string is that camo.
 */
export type Picker = {
  rkey: string | undefined | null;
  /**
   * A camo rolled here and kept nowhere: the bytes, when Random is what is
   * currently chosen, and null the rest of the time.
   *
   * It is not a record and has no key, so it cannot be an account default and
   * cannot be named. It rides on the launch itself and is gone afterwards -
   * the same bargain the bot's roll makes. Selecting anything else clears it,
   * because there is one selection.
   */
  rolled: Blob | null;
  node: Node;
  /**
   * The picture of what is chosen, for a swatch to draw beside the strip: the
   * collection's own image for a saved camo, the roll's object URL for a
   * roll, and null both for None and for "not chosen yet".
   */
  imageUrl: string | null;
  /**
   * What is chosen, named: the camo's own name, "None", or "Random". A
   * picture alone does not say whether a square of orange is a saved camo or
   * the roll, and a swatch that names what it is holding is the difference
   * between a control and a decoration.
   */
  label: string;
  /** Where each choosable camo's picture lives, filled as the list arrives. */
  images: Map<string, string>;
  /**
   * Told after every selection change, the same as `onChange` but for the
   * picker's own furniture rather than for the caller. `onChange` is one slot
   * and the caller owns it — `camoModal`'s swatch cannot borrow it without
   * taking it from the screen that is repainting its sprites.
   */
  watch: (fn: () => void) => void;
  /**
   * Told after every selection change, whether the player made it
   * (`true`) or the strip made it for them while nothing was chosen yet
   * (`false`) — see the module doc for why the distinction exists.
   */
  onChange: ((userInitiated: boolean) => void) | null;
};

/**
 * Which camo to fight in.
 *
 * Read here, in the browser, straight from the player's own repository — the
 * same public read the editor's collection does, and for the same reason. What
 * a caller does with the chosen record key is its own decision — see the
 * module doc — but the read, the strip and the three-state selection are the
 * same on every screen that shows this.
 *
 * `ownDefault` is whether this picker is choosing for the player's own force.
 * It is, everywhere the player is picking what *they* fight in; it is not on a
 * bot's force, which has no account and no default of its own, and where
 * opening on the player's marked camo would say the AI already wears it.
 *
 * What arrives selected is the account's default if it has one, and the newest
 * camo otherwise. With nothing of the player's to select at all - signed out,
 * or signed in with an empty collection - `paint` decides: `"none"` stands on
 * the grey factory scheme, `"random"` rolls one here and wears it. A player who has marked one in the editor gets it every
 * time without thinking about it; a player who has not gets the one they most
 * likely just finished. Either way the choice is one click to change and zero
 * to accept.
 */
export function camoPicker(
  session: Session | null,
  paint: "none" | "random" = "none",
  ownDefault = true,
): Picker {
  const watchers: (() => void)[] = [];
  const picker: Picker = {
    rkey: undefined,
    rolled: null,
    imageUrl: null,
    label: "None",
    node: el("div"),
    images: new Map(),
    watch: (fn) => {
      watchers.push(fn);
    },
    onChange: null,
  };
  const strip = el("div", { className: "camo-choices" });
  const note = el("p", {
    className: "hint",
    textContent: "Reading your camo…",
  });
  const back = el("button", {
    type: "button",
    className: "camo-page",
    textContent: "‹",
    "aria-label": "Previous page of camo",
  });
  const forward = el("button", {
    type: "button",
    className: "camo-page",
    textContent: "›",
    "aria-label": "Next page of camo",
  });
  const counter = el("span", { className: "camo-page-count" });
  const pager = el("div", { className: "camo-pager" }, [
    back,
    counter,
    forward,
  ]);
  pager.hidden = true;
  const block = el("div", { className: "choice camo-pick" }, [
    el("strong", { textContent: "Camo" }),
    note,
    strip,
    pager,
  ]);
  picker.node = block;

  /**
   * The tiles that stand in the strip whatever page it is showing: None, the
   * roll, and - when there is nothing saved to page through - the way to the
   * editor.
   */
  const fixed: Node[] = [];
  /** The saved camo, cut into pages of `PAGE_SIZE`. */
  let pages: HTMLButtonElement[][] = [];
  let page = 0;

  function renderStrip(): void {
    strip.replaceChildren(...fixed, ...(pages[page] ?? []));
    pager.hidden = pages.length < 2;
    counter.textContent = `${page + 1} / ${pages.length}`;
    back.disabled = page === 0;
    forward.disabled = page >= pages.length - 1;
  }

  /** Show a page, clamped: `pages` is empty until the collection lands. */
  function showPage(n: number): void {
    page = Math.min(Math.max(n, 0), Math.max(pages.length - 1, 0));
    renderStrip();
  }

  back.addEventListener("click", () => {
    showPage(page - 1);
  });
  forward.addEventListener("click", () => {
    showPage(page + 1);
  });

  // The none option is always there and always first. A player who wants the
  // plain colours should not have to own a camo to say so.
  const buttons: HTMLButtonElement[] = [];
  /**
   * Whether the player has chosen for themselves yet.
   *
   * The strip is drawn before the collection arrives, so None is clickable
   * during the fetch. Without this, the list landing a moment later would
   * overwrite that click and launch the match in a camo they had just
   * explicitly declined.
   */
  let chosenByPlayer = false;

  /** One selection, named as well as pictured - see `Picker.label`. */
  type Chosen = {
    rkey: string | null;
    label: string;
    userInitiated: boolean;
    rolled?: Blob | null;
    imageUrl?: string | null;
  };

  function choose(btn: HTMLButtonElement, chosen: Chosen): void {
    picker.rkey = chosen.rkey;
    picker.rolled = chosen.rolled ?? null;
    picker.imageUrl = chosen.imageUrl ?? null;
    picker.label = chosen.label;
    for (const other of buttons) {
      other.classList.toggle("active", other === btn);
      other.setAttribute("aria-pressed", String(other === btn));
    }
    for (const watcher of watchers) watcher();
    picker.onChange?.(chosen.userInitiated);
  }

  /** Select something only if the player has not already. */
  function preselect(
    btn: HTMLButtonElement,
    rkey: string | null,
    label: string,
    imageUrl: string | null = null,
  ): void {
    if (chosenByPlayer) return;
    choose(btn, { rkey, label, userInitiated: false, imageUrl });
  }

  function option(label: string, rkey: string | null, image?: string) {
    // Named in full on hover. The tile is a fixed square now, so a camo
    // called "i just liked this one" is cut to fit it and the whole name has
    // to be somewhere.
    const btn = el("button", {
      type: "button",
      className: "camo-choice",
      title: label,
    });
    btn.append(
      image
        ? el("img", { src: image, alt: "", width: 42, height: 36 })
        : el("span", { className: "camo-choice-none", textContent: "—" }),
      el("span", { className: "camo-choice-name", textContent: label }),
    );
    btn.addEventListener("click", () => {
      chosenByPlayer = true;
      choose(btn, {
        rkey,
        label,
        userInitiated: true,
        imageUrl: image ?? null,
      });
    });
    buttons.push(btn);
    return btn;
  }

  const plain = option("None", null);
  fixed.push(plain);

  /**
   * Roll one, and keep it exactly as long as the match setup lasts.
   *
   * Ephemeral on purpose. It writes nothing: no record, no blob in the
   * player's repository, no name to think of, and nothing to clean up
   * afterwards. It simply sits in the strip beside the camos that *are* saved,
   * and rides along with the launch as bytes - the same bargain the bot's roll
   * already makes.
   *
   * The tile draws from an object URL made here rather than from anything a
   * server hands back. That is also the bug this replaces: the first version
   * saved the roll and drew the tile from `create`'s answer, whose `imageUrl`
   * is null until the collection is listed again - so every roll came out as
   * the same empty tile and the whole thing looked like it was not rolling at
   * all.
   */
  function randomOption(): { rollFor: (userInitiated: boolean) => void } {
    // A tile and a die, not one button doing both. The tile is the camo: click
    // it to wear the one you already rolled, the same as every saved camo
    // beside it. The die is the only thing that rolls, so looking at a roll
    // you like and then selecting it cannot throw it away.
    // `camo-roll-tile`, not `camo-roll`: the editor's own roll button is
    // `.camo-roll`, and this span was quietly wearing that button's padding
    // and font size - which is why the Random tile never lined up with the
    // ones beside it.
    const wrap = el("span", { className: "camo-roll-tile" });
    const btn = el("button", {
      type: "button",
      className: "camo-choice camo-choice-roll",
    });
    const shown = el("img", { alt: "", width: 42, height: 36 });
    shown.hidden = true;
    const mark = el("span", { className: "camo-make-mark" }, [dieMark()]);
    mark.setAttribute("aria-hidden", "true");
    const label = el("span", {
      className: "camo-choice-name",
      textContent: "Random",
    });
    btn.append(mark, shown, label);
    const die = el(
      "button",
      {
        type: "button",
        className: "camo-die",
        "aria-label": "Roll a different camo",
        title: "Roll a different one",
      },
      [dieMark()],
    );
    wrap.append(btn, die);
    buttons.push(btn);
    fixed.push(wrap);

    /** The roll being worn, or null before the first one. */
    let held: Blob | null = null;
    // Revoked before the next one replaces it: an object URL is a document
    // -lifetime handle, and rolling twenty times should not leak twenty.
    let url: string | null = null;

    function roll(userInitiated = true): void {
      if (die.disabled) return;
      if (!userInitiated && chosenByPlayer) return;
      die.disabled = true;
      void (async () => {
        const { randomCamo, renderCamoPng } = await import("../camo/random");
        const blob = await renderCamoPng(randomCamo());
        if (url) URL.revokeObjectURL(url);
        url = URL.createObjectURL(blob);
        shown.src = url;
        shown.hidden = false;
        // Removed rather than hidden: `.camo-make-mark` sets `display`, and a
        // `display` in a stylesheet beats the `hidden` attribute - so hiding
        // it left the placeholder sitting above the camo it stood in for.
        mark.remove();
        held = blob;
        // A roll the strip made for a player who has nothing saved must not
        // count as a choice, or their first click on None would be overwritten
        // by a roll that was already in flight - and in a lobby it would write
        // an account default nobody asked for.
        if (!userInitiated && chosenByPlayer) return;
        chosenByPlayer ||= userInitiated;
        // No rkey: there is no record to point at, which is what "ephemeral"
        // means here and what keeps it out of the account default.
        choose(btn, {
          rkey: null,
          label: "Random",
          userInitiated,
          rolled: blob,
          imageUrl: url,
        });
      })()
        .catch((error: unknown) => {
          console.warn("camo picker: a camo could not be rolled", error);
          note.textContent = "That camo could not be rolled; pick another.";
        })
        .finally(() => {
          die.disabled = false;
        });
    }

    die.addEventListener("click", () => {
      roll();
    });
    btn.addEventListener("click", () => {
      // Before the first roll there is nothing to select, so the tile rolls
      // once to make one. After that it only ever selects.
      if (!held) {
        roll();
        return;
      }
      chosenByPlayer = true;
      choose(btn, {
        rkey: null,
        label: "Random",
        userInitiated: true,
        rolled: held,
        imageUrl: url,
      });
    });

    return { rollFor: roll };
  }
  const random = randomOption();
  renderStrip();

  /**
   * What stands selected when there is nothing of the player's to select.
   *
   * A roll rather than None. None is the grey factory scheme, and a screen
   * that opens on it opens on the least interesting thing it can draw; a
   * player who wants it is one click away, and a player who wants their own
   * camo has either marked a default or is about to sign in. The roll is
   * ephemeral either way, so nothing is written and nothing is kept.
   */
  function fallback(): void {
    if (chosenByPlayer) return;
    if (paint === "none") {
      preselect(plain, null, "None");
      return;
    }
    random.rollFor(false);
  }

  /**
   * The way out of an empty strip.
   *
   * A player with nothing to choose between was being told so and left there;
   * the editor is in the masthead, but reading a sentence about camo and then
   * going looking for where camo is made is a step this screen can save. A
   * tile rather than a line of text, so it sits in the strip beside None at
   * the size of the camo that will replace it — dashed, and with no image,
   * because it goes somewhere rather than being something to fight in.
   */
  function makeOne(): void {
    const link = el("a", { className: "camo-make", href: CAMO_HREF });
    // The plus stands in for the picture the other tiles have. Hidden from the
    // accessibility tree so the link is named "Design one" and not "+ Design
    // one".
    const mark = el("span", { className: "camo-make-mark", textContent: "+" });
    mark.setAttribute("aria-hidden", "true");
    link.append(
      mark,
      el("span", { className: "camo-choice-name", textContent: "Design one" }),
    );
    fixed.push(link);
    renderStrip();
  }

  // Signed out, the strip is what can be had without an account: None, and a
  // roll that is made here and kept nowhere. Saved camo is a read of somebody
  // else's repository and there is nobody to read — so the strip says that
  // rather than sitting empty, and Random is a real choice rather than a
  // consolation.
  if (!session) {
    note.textContent = "Sign in to fight in a camo you saved.";
    fallback();
    return picker;
  }

  // Lazily, and never fatally: this is the only thing on the screen that
  // needs another host, and a match must still be startable when it is down.
  void import("../camo/repo")
    .then((repo) => repo.list(session.did))
    .then((camos) => {
      if (!camos.length) {
        note.textContent = "You have not made a camo yet.";
        makeOne();
        fallback();
        return;
      }
      note.textContent = ownDefault
        ? "Your units will be painted in this."
        : "These units will be painted in this.";
      // list() is newest first, so index 0 is the newest camo.
      for (const camo of camos) {
        if (camo.imageUrl) picker.images.set(camo.rkey, camo.imageUrl);
      }
      const made = camos.map((camo) =>
        option(camo.name, camo.rkey, camo.imageUrl ?? undefined),
      );
      pages = [];
      for (let i = 0; i < made.length; i += PAGE_SIZE) {
        pages.push(made.slice(i, i + PAGE_SIZE));
      }
      // A force with no account behind it does not get the account's default:
      // the collection is there to choose from, but nothing in it is what
      // this force already wears. It opens on `paint` instead - see
      // `ownDefault`.
      if (!ownDefault) {
        showPage(0);
        fallback();
        return;
      }
      const marked = camos.findIndex(
        (camo) => camo.rkey === session.defaultCamo,
      );
      const start = marked === -1 ? 0 : marked;
      // Opened on the page holding what is selected, not on the first: a
      // default sitting on page three would otherwise be worn by a strip
      // showing no active tile at all.
      showPage(Math.floor(start / PAGE_SIZE));
      preselect(
        made[start]!,
        camos[start]!.rkey,
        camos[start]!.name,
        camos[start]!.imageUrl ?? null,
      );
    })
    .catch((error: unknown) => {
      // The picker stays on "not chosen", so a caller that reads it for a
      // launch or a claim still applies whatever default the account has.
      // Failing to read the list is no reason to launch in grey.
      console.warn("camo picker: camo could not be listed", error);
      note.textContent =
        "Your camo could not be read; the match can still start.";
    });

  return picker;
}

/**
 * The picker as a button and a dialog, for a screen with no room for a strip.
 *
 * The lobby puts this inside the force the player is taking, because that is
 * what the choice is about — these machines, painted in this — and a strip
 * across the bottom of the screen said it about nothing in particular. A
 * `<dialog>` and not a div: the browser already knows how to make one modal,
 * trap focus in it, close it on Escape and put it on the top layer, and every
 * hand-rolled version of that is worse.
 *
 * The picker's own node moves inside. There is one selection, not one per
 * place it is shown from.
 *
 * Two shapes for what opens it: a labelled button, and a swatch of what is
 * currently chosen for a header with no room for a word - see
 * `swatchButton`. The hangar takes the second, in its force's own heading.
 */
/**
 * The choice as a control: what is worn, what it is called, and the fact that
 * pressing it opens something.
 *
 * A bare square of camo was a picture. It drew what was chosen and said
 * nothing else - not that it could be pressed, not that the orange in it was
 * a saved camo rather than a roll - so it read as a thumbnail of the force
 * beside it. Three things fix that and none of them is a bigger square: the
 * word "Camo" over the name of what is chosen, a chevron saying a chooser
 * opens, and a frame that lifts under the pointer like every other button on
 * the screen.
 *
 * It follows the selection through `picker.watch` rather than `onChange`,
 * because the screen it sits on owns that slot - the hangar is repainting
 * every sprite it has drawn through it.
 */
function swatchButton(picker: Picker, label: string): HTMLButtonElement {
  const button = el("button", {
    type: "button",
    className: "secondary camo-open camo-chosen",
  });
  const shown = el("img", { className: "camo-chosen-art", alt: "" });
  const plain = el("span", { className: "camo-choice-none", textContent: "—" });
  const name = el("span", { className: "camo-chosen-name" });
  const caret = el("span", {
    className: "camo-chosen-caret",
    textContent: "▾",
  });
  caret.setAttribute("aria-hidden", "true");
  button.append(
    el("span", { className: "camo-chosen-art-box" }, [shown, plain]),
    el("span", { className: "camo-chosen-text" }, [
      el("span", { className: "camo-chosen-kicker", textContent: "Camo" }),
      name,
    ]),
    caret,
  );
  function paint(): void {
    const url = picker.imageUrl;
    if (url) shown.src = url;
    shown.hidden = !url;
    plain.hidden = !!url;
    name.textContent = picker.label;
    // Named in full to a screen reader and on hover, because the name itself
    // is one line in a control this size and a camo called "i just liked this
    // one" is cut to fit it.
    const said = `${label} (currently ${picker.label})`;
    button.title = said;
    button.setAttribute("aria-label", said);
  }
  paint();
  picker.watch(paint);
  return button;
}

export function camoModal(
  picker: Picker,
  label = "Choose camo",
  shape: "button" | "swatch" = "button",
): { button: HTMLButtonElement; dialog: HTMLDialogElement } {
  const dialog = el("dialog", { className: "camo-modal" });
  const done = el("button", {
    type: "button",
    className: "secondary small-btn",
    textContent: "Done",
  });
  done.addEventListener("click", () => dialog.close());
  dialog.append(
    el("div", { className: "camo-modal-body" }, [
      picker.node,
      el("div", { className: "row camo-modal-actions" }, [done]),
    ]),
  );
  // A click on the backdrop lands on the dialog itself rather than on
  // anything inside it, which is the only way to tell the two apart.
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });

  const button =
    shape === "swatch"
      ? swatchButton(picker, label)
      : el("button", {
          type: "button",
          className: "secondary small-btn camo-open",
          textContent: label,
        });
  button.addEventListener("click", () => dialog.showModal());
  return { button, dialog };
}

/**
 * A camo image from the player's own PDS, as pixels — for a preview to
 * paint a unit in, the way `scenario/preview.ts`'s compositor wants it.
 *
 * `crossOrigin` is what keeps the canvas readable: the PDS sends the CORS
 * header (the camo editor depends on the same thing), and without the
 * attribute the draw would taint the canvas and getImageData would throw.
 */
/**
 * The selection as pixels, for a screen that paints rather than launches.
 *
 * Both halves of the three-state selection resolve here: a saved camo through
 * the picture the collection already handed over, a roll through the bytes it
 * is holding. Null is the honest answer for None and for "not chosen yet" —
 * a caller draws bare sprite grey, which is what an unpainted machine is.
 */
export async function pickedPixels(picker: Picker): Promise<ImageData | null> {
  if (picker.rolled) return blobPixels(picker.rolled);
  if (typeof picker.rkey !== "string") return null;
  const url = picker.images.get(picker.rkey);
  return url ? remoteCamoPixels(url) : null;
}

/**
 * The selection as bytes, for a launch that carries a picture rather than a
 * key.
 *
 * A bot's force is the case: it has no repository, so there is no record for
 * a seat plan to name and the camo rides along as the file itself. A roll is
 * already bytes; a saved camo is fetched back from the picture the collection
 * handed over. Null is None, and a fetch that fails is None too - a match is
 * worth starting in plain colours.
 */
export async function pickedBlob(picker: Picker): Promise<Blob | null> {
  if (picker.rolled) return picker.rolled;
  if (typeof picker.rkey !== "string") return null;
  const url = picker.images.get(picker.rkey);
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`camo image: ${String(response.status)}`);
    return await response.blob();
  } catch (error: unknown) {
    console.warn("camo picker: a chosen camo could not be fetched", error);
    return null;
  }
}

/** Decode camo bytes to sprite-sized pixels for the compositor. */
async function blobPixels(blob: Blob): Promise<ImageData> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = 84;
  canvas.height = 72;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, 84, 72);
  bitmap.close();
  return ctx.getImageData(0, 0, 84, 72);
}

function remoteCamoPixels(url: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 84;
      canvas.height = 72;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(image, 0, 0, 84, 72);
      resolve(ctx.getImageData(0, 0, 84, 72));
    };
    image.onerror = () => reject(new Error("camo image did not load"));
    image.src = url;
  });
}
